import { useState, useEffect } from 'react';
import { invitationCodesAPI, employeesAPI, companiesAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Ticket, Plus, Trash2, Copy, Check, ShieldCheck, User } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

export default function InvitationCodesDialog({ open, onClose }) {
  const [codes, setCodes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ role: 'employee', employee_id: 'none', company_id: 'none' });
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (open) { fetchCodes(); fetchEmployees(); fetchCompanies(); }
  }, [open]);

  const fetchCodes = async () => {
    try { const res = await invitationCodesAPI.list(); setCodes(res.data); } catch { /* */ }
  };
  const fetchEmployees = async () => {
    try { const res = await employeesAPI.list(); setEmployees(res.data); } catch { /* */ }
  };
  const fetchCompanies = async () => {
    try { const res = await companiesAPI.list(); setCompanies(res.data); } catch { /* */ }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      const payload = {
        role: form.role,
        employee_id: form.employee_id !== 'none' ? form.employee_id : null,
        company_id: form.company_id !== 'none' ? form.company_id : null,
      };
      const res = await invitationCodesAPI.create(payload);
      toast.success(`Code créé : ${res.data.code}`);
      setShowCreate(false);
      setForm({ role: 'employee', employee_id: 'none', company_id: 'none' });
      fetchCodes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await invitationCodesAPI.delete(id);
      toast.success('Code supprimé');
      fetchCodes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleCopy = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    toast.success('Code copié !');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getEmployeeName = (id) => {
    const e = employees.find(e => e.id === id);
    return e ? `${e.first_name} ${e.last_name}` : '—';
  };
  const getCompanyName = (id) => companies.find(c => c.id === id)?.name || '—';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="invitation-codes-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="w-5 h-5" strokeWidth={1.5} />
            Codes d'inscription
          </DialogTitle>
          <DialogDescription>Générez des codes pour que vos employés puissent créer leur compte</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ScrollArea className="max-h-[280px]">
            <div className="space-y-2">
              {codes.map(c => (
                <div key={c.id} data-testid={`code-item-${c.id}`}
                  className={`flex items-center justify-between rounded-lg p-3 ${c.used ? 'bg-slate-50 opacity-60' : 'bg-slate-50'}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="text-sm font-bold font-data tracking-wider text-slate-900">{c.code}</code>
                      {c.role === 'admin' ? (
                        <Badge variant="destructive" className="text-[10px] h-5 px-1.5">Admin</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px] h-5 px-1.5">Employé</Badge>
                      )}
                      {c.used && <Badge variant="outline" className="text-[10px] h-5 px-1.5 text-slate-400">Utilisé</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400">
                      {c.employee_id && <span>Lié à {getEmployeeName(c.employee_id)}</span>}
                      {c.company_id && <span>Société: {getCompanyName(c.company_id)}</span>}
                      <span>Par {c.created_by}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {!c.used && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          data-testid={`copy-code-${c.id}`} onClick={() => handleCopy(c.code, c.id)}>
                          {copiedId === c.id ? <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={2} /> : <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700"
                          data-testid={`delete-code-${c.id}`} onClick={() => handleDelete(c.id)}>
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {codes.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Aucun code généré</p>}
            </div>
          </ScrollArea>

          <Separator />

          {showCreate ? (
            <div className="bg-slate-50 rounded-lg p-4 space-y-3 animate-fade-in-up">
              <Label className="text-sm font-semibold">Nouveau code d'inscription</Label>

              <div className="space-y-1">
                <Label className="text-xs">Rôle du compte</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="code-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">
                      <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" strokeWidth={1.5} />Employé</span>
                    </SelectItem>
                    <SelectItem value="admin">
                      <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} />Administrateur</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.role === 'employee' && (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs">Lier à un employé (optionnel)</Label>
                    <Select value={form.employee_id} onValueChange={v => setForm({ ...form, employee_id: v })}>
                      <SelectTrigger data-testid="code-employee-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {employees.map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.initials} - {e.first_name} {e.last_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Société (optionnel)</Label>
                    <Select value={form.company_id} onValueChange={v => setForm({ ...form, company_id: v })}>
                      <SelectTrigger data-testid="code-company-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" data-testid="code-cancel-btn"
                  onClick={() => { setShowCreate(false); setForm({ role: 'employee', employee_id: 'none', company_id: 'none' }); }}>
                  Annuler
                </Button>
                <Button size="sm" data-testid="code-generate-btn" onClick={handleCreate} disabled={loading}>
                  <Ticket className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                  {loading ? 'Génération...' : 'Générer le code'}
                </Button>
              </div>
            </div>
          ) : (
            <Button data-testid="create-code-btn" variant="outline" className="w-full" onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Générer un code d'inscription
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
